import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigationStatus } from '../hooks/useNavigationStatus';
import './AllAlarms.css';
import './AllAlarmrules.css';
import { API_BASE, authHeaders } from '../constants/api';
import { severityLabels, severityOrder, entityTypeLabels, getSeverities } from '../constants/alarmConstants';
import { formatDateDE } from '../utils/formatDate';
import Pagination from './Pagination';
import SearchWithSuggestions from './SearchWithSuggestions';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import { Hinzufügen } from '../icons';
import { useStatusMessage } from '../hooks/useStatusMessage';
import { StatusBar, DeleteConfirmModal, SortDropdown, IconButton, LoadingErrorState, PageToolbar } from '../UI-Elements';

const PAGE_SIZE = 10;

const _DEDUPE_MS = 500;



function AllAlarmrules() {
  const [rules, setRules] = useState([]);
  const [rulesCount, setRulesCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [sortOption, setSortOption] = useState('createdTime');

  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const dedupeRef = useRef({ key: null, time: 0 });

  const { statusMessage, statusType, showStatus } = useStatusMessage();

  const { authToken: token } = useAuth();
  const navigate = useNavigate();

  useNavigationStatus(showStatus);

  useEffect(() => {
    async function loadRules() {
      try {
        setLoading(true);
        setError("");

        const key = `${token || ''}|${page}|${sortOption}|${searchTerm || ''}`;
        const now = Date.now();
        if (dedupeRef.current.key === key && now - dedupeRef.current.time < _DEDUPE_MS) return;
        dedupeRef.current = { key, time: now };

        const params = new URLSearchParams();
        params.append('pageSize', String(PAGE_SIZE));
        params.append('page', String(page));
        params.append('types', 'ALARM');
        params.append('sortProperty', 'createdTime');
        params.append('sortOrder', 'DESC');
        if (searchTerm) params.append('textSearch', searchTerm);

        const res = await fetch(`${API_BASE}/calculatedFields?${params.toString()}`, {
          headers: authHeaders(token)
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Fehler beim Laden der Alarmregeln (HTTP ${res.status})`);
        }
        const data = await res.json();
        let items = data.data || [];

        // Client-side sort for non-createdTime options
        if (sortOption === 'name') {
          items.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        } else if (sortOption === 'severity') {
          items.sort((a, b) => {
            const sa = Math.min(...getSeverities(a).map(s => severityOrder[s] ?? 99));
            const sb = Math.min(...getSeverities(b).map(s => severityOrder[s] ?? 99));
            return sa - sb;
          });
        } else if (sortOption === 'entityType') {
          items.sort((a, b) => (a.entityId?.entityType || '').localeCompare(b.entityId?.entityType || ''));
        }

        setRules(items);
        setRulesCount(data.totalElements);
        setTotalPages(data.totalPages);
      } catch (err) {
        setError(err.message || "Fehler beim Laden der Alarmregeln. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        setRules([]);
        setRulesCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    }
    if (token) loadRules();
  }, [token, page, searchTerm, sortOption, reloadKey]);

  const suggestions = searchTerm
    ? (() => {
        const seen = new Set();
        const list = [];
        for (const r of rules) {
          const name = r.name || '';
          if (name.toLowerCase().includes(searchTerm.toLowerCase()) && !seen.has(name)) {
            seen.add(name);
            list.push({ id: r.id?.id, name });
            if (list.length >= 5) break;
          }
        }
        return list;
      })()
    : [];

  async function deleteRule() {
    if (!ruleToDelete) return;
    try {
      setDeleting(true);
      const res = await fetch(`${API_BASE}/calculatedField/${ruleToDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      showStatus(`Alarmregel „${ruleToDelete.name}" wurde erfolgreich gelöscht.`);
      setReloadKey(k => k + 1);
    } catch (err) {
      showStatus(`Alarmregel konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setRuleToDelete(null);
    }
  }

  return (
    <div className="all-alarms-page">
      <PageToolbar
        left={
          <>
            <h1 className="device-title">Alarmregeln ({rulesCount ?? "..."})</h1>
            <SortDropdown
              options={[
                { key: 'name', label: 'Bezeichnung' },
                { key: 'createdTime', label: 'Erstellt' },
                { key: 'severity', label: 'Schweregrad' },
                { key: 'entityType', label: 'Typ' },
              ]}
              value={sortOption}
              onChange={(key) => { setSortOption(key); setPage(0); }}
            />
          </>
        }
        right={
          <>
            <button className="export-button" aria-label="Neue Alarmregel erstellen" title="Neue Alarmregel erstellen" onClick={() => navigate('/new-alarm-rule')}>
              <div className="export-icon"><Hinzufügen /></div>
              <div>Neue Alarmregel</div>
            </button>
            <SearchWithSuggestions
              ref={searchInputRef}
              id="alarmrule-search"
              label="Alarmregel suchen"
              value={searchTerm}
              onChange={(val) => { setSearchTerm(val); setPage(0); }}
              suggestions={suggestions}
              suggestionsId="alarmrule-suggestions"
            />
          </>
        }
      />

      <StatusBar message={statusMessage} statusType={statusType} />

      {/* Liste */}
      <div className="device-table-container" role="region" aria-label="Alarmregeln" aria-busy={loading}>
        <LoadingErrorState loading={loading} loadingText="Alarmregeln werden geladen…" error={error}>
          <>
            <div className="device-list alarmrules-list" role="list" aria-label="Alarmregeln">
              <div className="device-row device-header" role="presentation" aria-hidden="true">
                <div>Bezeichnung</div><div>Regel für</div><div>Endgerät/Gerättyp</div>
                <div>Erstellt</div><div>Öffnen</div><div>Löschen</div>
              </div>
              {rules.length === 0 ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Alarmregeln gefunden</div>
              ) : rules.map(r => {
                const severities = getSeverities(r);
                const entityType = r.entityId?.entityType;
                const entityId = r.entityId?.id;
                const isDevice = entityType === 'DEVICE';
                return (
                  <div key={r.id?.id} className="device-row device-item clickable-row" role="listitem"
                    tabIndex={0} aria-label={`Alarmregel ${r.name} öffnen`}
                    onClick={(e) => { if (e.target.closest('button, .originator-link')) return; navigate(`/alarm-rules/${r.id?.id}`); }}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .originator-link')) { e.preventDefault(); navigate(`/alarm-rules/${r.id?.id}`); } }}
                  >
                    <div>
                      <span className="alarm-name">{r.name}</span>
                      {severities.map(s => <span key={s} className={`severity-badge ${s.toLowerCase()}`}>{severityLabels[s] ?? s}</span>)}
                    </div>
                    <div>{entityTypeLabels[entityType] ?? entityType ?? '—'}</div>
                    <div>
                      {isDevice && entityId
                        ? <button type="button" className="originator-link" title={`Gerät ${r.entityName} öffnen`} aria-label={`Gerät ${r.entityName} öffnen`} onClick={e => { e.stopPropagation(); navigate(`/devices/${entityId}`, { state: { name: r.entityName } }); }}>{r.entityName}</button>
                        : <span>{r.entityName ?? '—'}</span>}
                    </div>
                    <div>{formatDateDE(r.createdTime)}</div>
                    <div className="cell-right">
                      <IconButton variant="open" label="Details öffnen" onClick={() => navigate(`/alarm-rules/${r.id?.id}`)} />
                    </div>
                    <div className="cell-right">
                      <IconButton variant="delete" label={`Alarmregel ${r.name} löschen`}
                        onClick={() => { setRuleToDelete({ id: r.id?.id, name: r.name }); setShowDeleteConfirm(true); }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {rulesCount > 0 && <Pagination page={page} totalPages={totalPages} totalItems={rulesCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Einträge" />}
          </>
        </LoadingErrorState>
      </div>
      {showDeleteConfirm && ruleToDelete && (
        <DeleteConfirmModal
          titleId="delete-rule-title"
          title="Alarmregel löschen?"
          body={<p>Soll die Alarmregel <i>{ruleToDelete.name}</i> wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>.</p>}
          onConfirm={deleteRule}
          onCancel={() => { setShowDeleteConfirm(false); setRuleToDelete(null); }}
          loading={deleting}
        />
      )}
    </div>
  );
}

export default function AllAlarmrulesWithBoundary() {
  return <ErrorBoundary><AllAlarmrules /></ErrorBoundary>;
}
